/**
 * Removes nullish values from an array
 */
export function compact<T>(array: (T | null | undefined)[]): T[] {
  return array.filter((v) => v != null) as NonNullable<T>[];
}

/**
 * Similar to `compact`, but also checks for empty strings
 */
export function compactString<T>(array: (T | null | undefined | false)[]): T[] {
  return array.filter((v) => !!v) as NonNullable<T>[];
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Non-empty strings of an unknown value, empty when it isn't an array
 */
export function nonEmptyStringsIn(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(isNonEmptyString) : [];
}

/**
 * Raises a typescript error + throws if a branch that should be unreachable
 * is, infact, reachable. Can be used for example in switch statements to ensure
 * all options are covered.
 */
export function assertUnreachable(
  _: never,
  message: string = "invariant: Reached unreachable branch or condition",
): never {
  throw new Error(message);
}

/**
 * Tries to split a string containing a full name into parts. Very
 * iffy and sketchy.
 */
export function splitFullName(fullName: string) {
  const name = fullName.split(" ");

  return {
    lastName: name[name.length - 1],
    firstName: name.slice(0, -1).join(" "),
  };
}

/**
 * Deletes the object keys that have null or undefined values.
 * @param obj
 * @returns The input object with valid values
 */
export function cleanObject(obj: Record<string, unknown>) {
  return Object.entries(obj).reduce(
    (a, [key, value]) => (value == null ? a : ((a[key] = value), a)),
    {} as Record<string, unknown>,
  );
}

/**
 * Truncates a string to a specified length and appends an ellipsis if truncated.
 */
export function truncateText(text?: string, maxLength = 256) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Returns a normalized version of the input string.
 *
 * @param text
 * @returns - The normalized text
 */
export function normalizeText(text: string) {
  return text.trim().toLowerCase();
}

export function sameText(a: string, b: string) {
  return normalizeText(a) === normalizeText(b);
}

/**
 * Same items in any order, for arrays without duplicates
 */
export function sameSet(left: string[], right: string[]) {
  return left.length === right.length && left.every((id) => right.includes(id));
}

export function toTitleCase(text: string) {
  return text.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase(),
  );
}

export function toCamelCase(text: string) {
  return text.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

/**
 * Converts object keys to camelCase, nested objects and arrays included
 */
export function camelizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelizeKeys);
  if (value === null || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      toCamelCase(key),
      camelizeKeys(inner),
    ]),
  );
}

export function mapValues<T, U>(
  record: Record<string, T>,
  map: (value: T) => U,
): Record<string, U> {
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, map(value)]),
  );
}

// Non-safe unique ID generator
export function uniqueId(prefix?: string) {
  return `${prefix || ""}${Math.random().toString(16).slice(2)}`;
}

/**
 * Returns the singular or plural word form for an array (using its length) or a raw count.
 */
export function pluralizeWord(
  countOrItems: number | unknown[],
  singular: string,
  plural = `${singular}s`,
) {
  const count = Array.isArray(countOrItems)
    ? countOrItems.length
    : countOrItems;
  return count === 1 ? singular : plural;
}

/**
 * Returns "<count> <singular|plural>" for an array (using its length) or a raw count.
 */
export function pluralize(
  countOrItems: number | unknown[],
  singular: string,
  plural = `${singular}s`,
) {
  const count = Array.isArray(countOrItems)
    ? countOrItems.length
    : countOrItems;
  return `${count} ${pluralizeWord(count, singular, plural)}`;
}
